import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { cn } from "../../lib/utils"
import { ColorIcon } from "../Icon/ColorIcon"
import { colorIconComponents } from "../Icon/colorIconRenderer"
import { Icon } from "../Icon/Icon"
import { Spinner } from "../Icon/Spinner"
import type { ColorIconName, IconName } from "../Icon/types"

const buttonStyles =
  "relative rounded-cx-button-radius-base inline-flex items-center justify-center gap-2 whitespace-nowrap font-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-base-border-focus focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-icon-sm [&_svg]:shrink-0"

export const buttonVariants = cva(buttonStyles, {
  variants: {
    kind: {
      text: "",
      "icon-only": "p-0",
    },
    variant: {
      primary: [
        "bg-action-primary-background-default text-root-inverse-text-base",
        "hover:bg-action-primary-background-hover",
        "focus:bg-action-primary-background-focus",
        "active:bg-action-primary-background-active",
      ],
      secondary: [
        "bg-action-secondary-background-default text-root-inverse-text-base",
        "hover:bg-action-secondary-background-hover",
        "focus:bg-action-secondary-background-focus",
        "active:bg-action-secondary-background-active",
      ],
      tertiary: [
        "bg-action-tertiary-background-default text-action-base-text",
        "hover:bg-action-tertiary-background-hover",
        "focus:bg-action-tertiary-background-focus",
        "active:bg-action-tertiary-background-active",
      ],
      ghost: [
        "bg-action-ghost-background-default text-action-base-text",
        "hover:bg-action-ghost-background-hover",
        "focus:bg-action-ghost-background-focus",
        "active:bg-action-ghost-background-active",
      ],
      "ghost-inverse": [
        "bg-action-ghost-inverse-background-default text-root-inverse-text-base",
        "hover:bg-action-ghost-inverse-background-hover",
        "focus:bg-action-ghost-inverse-background-focus",
        "active:bg-action-ghost-inverse-background-active",
      ],
      danger: [
        "bg-action-danger-background-default text-root-inverse-text-base",
        "hover:bg-action-danger-background-hover",
        "focus:bg-action-danger-background-focus",
        "active:bg-action-danger-background-active",
      ],
    },
    size: {
      xl: cn(
        "py-cx-button-spacing-top-xl px-cx-button-spacing-side-xl",
        "[&[data-dropdown]>svg]:right-6 data-[dropdown]:pr-12",
        "text-root-size-body-lg leading-root-line-height-body-md font-500"
      ),
      lg: cn(
        "py-cx-button-spacing-top-lg px-cx-button-spacing-side-lg",
        "[&[data-dropdown]>svg]:right-4  data-[dropdown]:pr-cx-button-spacing-side-md",
        "text-root-size-body-lg leading-root-line-height-body-md font-500"
      ),
      md: cn(
        "py-cx-button-spacing-top-md px-cx-button-spacing-side-md",
        "[&[data-dropdown]>svg]:right-3 data-[dropdown]:pr-8",
        "text-root-size-body-lg leading-root-line-height-body-md font-500"
      ),
      sm: cn(
        "py-cx-button-spacing-top-sm px-cx-button-spacing-side-sm",
        "[&[data-dropdown]>svg]:right-[6px] data-[dropdown]:pr-6",
        "text-root-size-body-lg leading-root-line-height-body-md font-500"
      ),
    },
  },
  defaultVariants: {
    variant: "primary",
    size: "md",
    kind: "text",
  },
})

const iconButtonVariants = cva(buttonStyles, {
  variants: {
    variant: {
      primary: [
        "bg-action-primary-background-default text-root-inverse-text-base",
        "hover:bg-action-primary-background-hover",
        "focus:bg-action-primary-background-focus",
        "active:bg-action-primary-background-active",
      ],
      secondary: [
        "bg-action-secondary-background-default text-root-inverse-text-base",
        "hover:bg-action-secondary-background-hover",
        "focus:bg-action-secondary-background-focus",
        "active:bg-action-secondary-background-active",
      ],
      tertiary: [
        "bg-action-tertiary-background-default text-action-base-text",
        "hover:bg-action-tertiary-background-hover",
        "focus:bg-action-tertiary-background-focus",
        "active:bg-action-tertiary-background-active",
      ],
      ghost: [
        "bg-action-ghost-background-default text-action-base-text",
        "hover:bg-action-ghost-background-hover",
        "focus:bg-action-ghost-background-focus",
        "active:bg-action-ghost-background-active",
      ],
      "ghost-inverse": [
        "bg-action-ghost-inverse-background-default text-root-inverse-text-base",
        "hover:bg-action-ghost-inverse-background-hover",
        "focus:bg-action-ghost-inverse-background-focus",
        "active:bg-action-ghost-inverse-background-active",
      ],
      danger: [
        "bg-action-danger-background-default text-root-inverse-text-base",
        "hover:bg-action-danger-background-hover",
        "focus:bg-action-danger-background-focus",
        "active:bg-action-danger-background-active",
      ],
    },
    size: {
      xl: "size-12 p-cx-button-spacing-top-xl",
      lg: "size-10 py-cx-button-spacing-top-lg px-cx-button-spacing-side-sm",
      md: "size-8 p-2",
      sm: "size-6 p-cx-button-spacing-top-sm",
    },
  },
  defaultVariants: {
    variant: "primary",
    size: "md",
  },
})

// this json isnt real code just info on how we can use button with text, or only icon. the icon-only variant is a round thing basically
// the text variant can have iconStart prop that can render an icon before the text
// types: {
//   text: "",
//   "icon-only": "",
// },

type ButtonBaseProps = {
  asChild?: boolean
}

type ButtonWithIcons = ButtonBaseProps & {
  iconEnd?: IconName | ColorIconName
  iconStart?: IconName | ColorIconName
  loading?: boolean
}

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> &
  ButtonWithIcons

function getIconEl(icon: IconName | ColorIconName, isDropdown?: boolean): React.ReactNode {
  if (icon in colorIconComponents) {
    return <ColorIcon name={icon as ColorIconName} />
  }

  // Handle ChevronDown rotation for dropdown buttons using CSS
  if (isDropdown) {
    return (
      <Icon
        name={icon as IconName}
        className="transition-transform origin-center group-aria-[expanded=true]:rotate-180"
      />
    )
  }

  return <Icon name={icon as IconName} />
}

function sanitizeProps(props: ButtonProps) {
  const updatedProps = { ...props }

  if ("iconStart" in updatedProps) {
    delete updatedProps.iconStart
  }

  if ("iconEnd" in updatedProps) {
    delete updatedProps.iconEnd
  }

  if ("loading" in updatedProps) {
    delete updatedProps.loading
  }

  return updatedProps
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild, children, loading, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const sanitizedProps = sanitizeProps(props)

    // Detect if this is a dropdown button with aria-expanded state
    const isDropdown =
      "iconEnd" in props &&
      props.iconEnd === "ChevronDown" &&
      className?.includes("buoy-dropdown-btn")
    // When loading, show spinner in iconStart position, otherwise use original icons
    const iconStartElement = loading ? (
      <Spinner className="w-[1em] h-[1em]" />
    ) : (
      "iconStart" in props && props.iconStart && getIconEl(props.iconStart)
    )

    const iconEndElement = loading
      ? null // Don't show iconEnd when loading
      : "iconEnd" in props && props.iconEnd && getIconEl(props.iconEnd, isDropdown)

    if (iconStartElement && iconEndElement) {
      throw new Error("invalid button config, please decide where you want to place the icon")
    }

    // Disable onClick when loading
    const handleClick = loading ? () => {} : props.onClick

    const iconAwareClasses = getIconAwareClasses({
      hasIconStart: Boolean(iconStartElement),
      hasIconEnd: Boolean(iconEndElement),
      size: size || "md",
    })

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }), iconAwareClasses)}
        ref={ref}
        {...sanitizedProps}
        onClick={handleClick}
        disabled={loading || props.disabled}
      >
        {iconStartElement}
        {children}
        {iconEndElement}
      </Comp>
    )
  }
)

Button.displayName = "Button"

export type IconButtonProps = Omit<ButtonProps, "children" | "iconStart" | "iconEnd"> & {
  icon: IconName | React.ReactElement
}

export const IconButtonInternal = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, size = "md", icon, ...props }, ref) => {
    return (
      <button className={cn(iconButtonVariants({ variant, size, className }))} ref={ref} {...props}>
        {typeof icon === "string" ? <Icon name={icon} /> : icon}
      </button>
    )
  }
)

IconButtonInternal.displayName = "IconButtonShadcn"

export function getIconAwareClasses({
  hasIconStart,
  hasIconEnd,
  size,
}: {
  hasIconStart: boolean
  hasIconEnd: boolean
  size: "xl" | "lg" | "md" | "sm"
}): string {
  if (!hasIconStart && !hasIconEnd) return ""

  const paddingMap = {
    xl: { reduced: "cx-button-spacing-side-lg", normal: "cx-button-spacing-side-xl" },
    lg: { reduced: "cx-button-spacing-side-md", normal: "cx-button-spacing-side-lg" },
    md: { reduced: "cx-button-spacing-side-sm", normal: "cx-button-spacing-side-md" },
    sm: { reduced: "[0.375rem]", normal: "cx-button-spacing-side-sm" },
  }

  const { reduced, normal } = paddingMap[size]

  if (hasIconStart && hasIconEnd) {
    return `pl-${reduced} pr-${reduced}`
  }

  if (hasIconStart) {
    return `pl-${reduced} pr-${normal}`
  }

  if (hasIconEnd) {
    return `pl-${normal} pr-${reduced}`
  }

  return ""
}
